import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Landmark, X, CheckCircle2, Smartphone } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { toast } from '../../hooks/use-toast';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const empty = {
  bank_name: '',
  holder_name: '',
  account_number: '',
  ifsc: '',
  upi_vpa: '',
  property_id: '',
  account_type: 'savings',
  is_primary_for_property: false,
  is_settlement_account: false,
  notes: '',
};

const BankFormModal = ({ open, onClose, onSaved, properties = [], token, editing, defaultPropertyId }) => {
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (editing) {
      setForm({
        ...empty,
        ...editing,
        property_id: editing.property_id || '',
        upi_vpa: editing.upi_vpa || '',
        notes: editing.notes || '',
      });
    } else {
      setForm({ ...empty, property_id: defaultPropertyId || '' });
    }
  }, [editing, defaultPropertyId, open]);

  const up = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.bank_name.trim()) return toast({ title: 'Bank name required', variant: 'destructive' });
    if (!form.holder_name.trim()) return toast({ title: 'Holder name required', variant: 'destructive' });
    if (!/^\d{6,20}$/.test(form.account_number.trim())) {
      return toast({ title: 'Account number must be 6–20 digits', variant: 'destructive' });
    }
    if (!/^[A-Z]{4}0[A-Z0-9]{6}$/i.test(form.ifsc.trim())) {
      return toast({ title: 'IFSC invalid', description: 'Format: 4 letters + 0 + 6 alphanumeric (e.g. HDFC0001234)', variant: 'destructive' });
    }
    if (form.upi_vpa && !/^[\w.\-]{2,256}@[a-zA-Z]{2,64}$/.test(form.upi_vpa.trim())) {
      return toast({ title: 'UPI VPA invalid', description: 'Format: name@bank (e.g. raj@okhdfcbank)', variant: 'destructive' });
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        property_id: form.property_id || null,
        upi_vpa: form.upi_vpa.trim() || null,
        ifsc: form.ifsc.trim().toUpperCase(),
        account_number: form.account_number.trim().replace(/\s/g, ''),
        notes: form.notes.trim() || null,
      };
      const headers = { Authorization: `Bearer ${token}` };
      if (editing?.id) {
        await axios.patch(`${API}/banks/${editing.id}`, payload, { headers });
        toast({ title: 'Bank updated' });
      } else {
        await axios.post(`${API}/banks`, payload, { headers });
        toast({ title: 'Bank added' });
      }
      onSaved();
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail.map(d => d.msg).join(', ') : (detail || 'Save failed');
      toast({ title: 'Save failed', description: msg, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Landmark size={20} className="text-emerald-600" />
            {editing ? 'Edit bank account' : 'Add bank / UPI'}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4" data-testid="bank-form">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Bank name *</Label>
              <Input data-testid="bf-bank" value={form.bank_name} onChange={e => up('bank_name', e.target.value)} placeholder="HDFC Bank" required />
            </div>
            <div>
              <Label>Account holder *</Label>
              <Input data-testid="bf-holder" value={form.holder_name} onChange={e => up('holder_name', e.target.value)} placeholder="Raj Kumar" required />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Account number *</Label>
              <Input
                data-testid="bf-acct"
                value={form.account_number}
                onChange={e => up('account_number', e.target.value.replace(/\D/g, '').slice(0, 20))}
                placeholder="123456789012"
                required
              />
            </div>
            <div>
              <Label>IFSC *</Label>
              <Input
                data-testid="bf-ifsc"
                value={form.ifsc}
                onChange={e => up('ifsc', e.target.value.toUpperCase().slice(0, 11))}
                placeholder="HDFC0001234"
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Account type</Label>
              <select
                data-testid="bf-type"
                value={form.account_type}
                onChange={e => up('account_type', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              >
                <option value="savings">Savings</option>
                <option value="current">Current</option>
              </select>
            </div>
            <div>
              <Label className="flex items-center gap-1"><Smartphone size={12} /> UPI VPA (optional)</Label>
              <Input
                data-testid="bf-upi"
                value={form.upi_vpa}
                onChange={e => up('upi_vpa', e.target.value.toLowerCase())}
                placeholder="raj@okhdfcbank"
              />
            </div>
          </div>

          <div>
            <Label>Linked property (optional)</Label>
            <select
              data-testid="bf-property"
              value={form.property_id}
              onChange={e => up('property_id', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
            >
              <option value="">— No property (global) —</option>
              {properties.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
            <p className="text-[11px] text-gray-500 mt-1">Linking lets you mark this as the primary account on rent invoices for that property.</p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <label className="flex items-start gap-2 p-2.5 border border-gray-200 rounded-lg cursor-pointer hover:bg-amber-50/50">
              <input
                data-testid="bf-primary"
                type="checkbox"
                checked={form.is_primary_for_property}
                onChange={e => up('is_primary_for_property', e.target.checked)}
                disabled={!form.property_id}
                className="mt-0.5 accent-amber-600"
              />
              <div>
                <p className="text-sm font-medium text-gray-900">Primary for property</p>
                <p className="text-[11px] text-gray-500">{form.property_id ? 'Show on rent invoices' : 'Link a property first'}</p>
              </div>
            </label>
            <label className="flex items-start gap-2 p-2.5 border border-gray-200 rounded-lg cursor-pointer hover:bg-emerald-50/50">
              <input
                data-testid="bf-settlement"
                type="checkbox"
                checked={form.is_settlement_account}
                onChange={e => up('is_settlement_account', e.target.checked)}
                className="mt-0.5 accent-emerald-600"
              />
              <div>
                <p className="text-sm font-medium text-gray-900">Settlement account</p>
                <p className="text-[11px] text-gray-500">Cashfree / online payouts arrive here</p>
              </div>
            </label>
          </div>

          <div>
            <Label>Notes (optional)</Label>
            <Input
              data-testid="bf-notes"
              value={form.notes}
              onChange={e => up('notes', e.target.value)}
              placeholder="e.g., NEFT cutoff 6pm, branch contact"
            />
          </div>

          <div className="flex justify-end gap-2 pt-2 border-t">
            <Button type="button" variant="outline" onClick={onClose}><X size={14} className="mr-1" /> Cancel</Button>
            <Button type="submit" disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="bf-submit">
              {saving ? 'Saving…' : (<><CheckCircle2 size={14} className="mr-1" /> {editing ? 'Save changes' : 'Add bank'}</>)}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default BankFormModal;
